import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PiClock, PiCompass, PiList } from 'react-icons/pi'

import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'

import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import { ChainsTable } from 'components/Tables/ChainsTable/ChainsTable'
import { CompaniesTable } from 'components/Tables/CompaniesTable/CompaniesTable'
import { HospitalsTable } from 'components/DataExpansionComponents/Hospitals/HospitalsTable/HospitalsTable'
import { K12Table } from 'components/DataExpansionComponents/K12/K12Table/K12Table'
import { UniversitiesTable } from 'components/DataExpansionComponents/Universities/UniversitiesTable/UniversitiesTable'
import { NursingHomesTable } from 'components/DataExpansionComponents/NursingHomes/NursingHomeTable/NursingHomesTable'
import { LodgingTable } from 'components/DataExpansionComponents/GooglePlaces/Lodging/LodgingTable/LodgingTable'
import { ConvenienceTable } from 'components/DataExpansionComponents/GooglePlaces/Convenience/ConvenienceTable/ConvenienceTable'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CHAINS_TABLE_KEY,
  COLLEGE_AND_UNIVERSITY_TABLE_KEY,
  CONVENIENCE_TABLE_KEY,
  getCampaignTableKey,
  getCompanyTableKey,
  HOSPITALS_TABLE_KEY,
  K12_TABLE_KEY,
  LODGING_TABLE_KEY,
  NURSING_HOMES_TABLE_KEY,
} from 'constants/tableQueryKeys'
import {
  Category,
  CompanyTypeSlug,
  CompanyTypeSlugWithFBData,
  SubCategory,
} from 'models/companies'
import { Campaign } from 'models/campaign'
import { FilterIdentifier } from 'models/saved_view'
import { ChainsSummary } from 'models/summaries'
import { StandardSummaryResponse } from 'models/paginated-response'
import apiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import FbButton from 'components/FbUI/FbButton'
import { useGetCampaignCompanies } from './campaigns_api'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'

import { MissingContactsBanner } from './MissingContactsBanner'

const NOOP_SUMMARY: StandardSummaryResponse = { count: 0, alt_count: 0 }

interface CampaignDetailTargetsTabProps {
  campaign: Campaign
}

export function CampaignDetailTargetsTab({
  campaign,
}: CampaignDetailTargetsTabProps) {
  const navigate = useNavigate()
  const featureFlag = useFeatureFlagService()
  const api = apiService()
  const { data: campaignCompanies, isLoading: targetsLoading } =
    useGetCampaignCompanies(campaign.id)
  const { data: categories, isFetching: categoriesLoading } =
    api.useGetCompanyCategories()

  const [, setChainsSummary] = useState<ChainsSummary>({
    isLoading: true,
    total_ltv: 0,
    count: 0,
    places_count: 0,
    avg_pounds_per_year: 0,
    avg_total_ltv: 0,
  })
  const [, setRowCount] = useState<StandardSummaryResponse>(NOOP_SUMMARY)

  const campaignTypes = useMemo(() => {
    const types: {
      slug: string
      sub_category: SubCategory
      category: Category
    }[] = []
    categories?.forEach((cat) => {
      cat.sub_categories.forEach((s) => {
        types.push({ slug: s.slug, sub_category: s, category: cat })
      })
    })
    return types
  }, [categories])

  const type = campaignTypes?.find((t) => t?.slug === campaign?.type?.slug)

  if (targetsLoading || categoriesLoading || (!type && campaign.type)) {
    return <LoadingScreen />
  }

  if ((campaignCompanies?.total_count ?? 0) === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-line bg-surface p-6">
          <h3 className="text-base text-fg">Add Targets</h3>
          <p className="mt-0.5 mb-3 text-xs text-fg-secondary">
            Add targets to this campaign by browsing all available targets.
          </p>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex h-full w-full flex-col rounded-xl border border-line">
              <div className="flex flex-1 flex-col items-center p-5 pb-3 text-center">
                <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-brand-active/10">
                  <PiCompass className="size-5 text-brand-active" />
                </div>
                <span className="mb-1 text-sm text-fg">Explore Targets</span>
                <span className="text-xs text-fg-secondary">
                  Browse and filter all available targets
                </span>
              </div>

              <div className="mt-auto flex justify-center px-4 pb-4">
                <FbButton
                  type="button"
                  className="w-[240px] !bg-brand-bright !text-white hover:!bg-brand-active"
                  onClick={() => navigate('/targets')}
                >
                  <TargetArrowIcon size={18} />
                  Browse All Targets
                </FbButton>
              </div>
            </div>

            <div className="flex h-full w-full flex-col rounded-xl border border-line bg-surface-alt">
              <div className="flex flex-1 flex-col items-center p-5 pb-3 text-center">
                <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-surface-hover">
                  <PiList className="size-5 text-fg-muted" />
                </div>
                <span className="mb-1 text-sm text-fg-secondary">
                  Choose from List
                </span>
                <span className="text-xs text-fg-muted">
                  Assign targets from a saved target list
                </span>
                <span className="mt-3 rounded-full bg-surface-hover px-2 py-0.5 text-xxs uppercase tracking-wider text-fg-secondary">
                  Coming Soon
                </span>
              </div>

              <div className="mt-auto flex justify-center px-4 pb-4">
                <FbButton
                  type="button"
                  variant="secondary"
                  className="w-[240px] !border-neutral-300"
                  disabled
                >
                  <PiClock className="size-4" />
                  Coming Soon
                </FbButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const filterMod = `_CAMPAIGN_${campaign.id}` as FilterIdentifier
  const useDenormalized = featureFlag.enableCampaignDenormalizedStats

  const tableBySlugType: Record<CompanyTypeSlugWithFBData, React.JSX.Element> =
    {
      'restaurants-bars': (
        <ChainsTable
          setChainsSummary={setChainsSummary}
          hideCreateCompanyButton
          filterIdentifierModifier={filterMod}
          useReports={useDenormalized ? false : undefined}
          defaultColumnVisibility={{
            chain: true,
            count: true,
            menu: false,
            menu_length: false,
            menu_matches: false,
            total_ltv: false,
            brand_ltv: false,
            revenue_ltv: false,
            one_year_total_value: false,
            taro: false,
            tabo: false,
            pounds_per_year: false,
            note_count: true,
            contact_count: true,
            deal_count: true,
            sales_stages: true,
            account_owners: true,
            tags: true,
            distributors: true,
            cuisine_50: false,
            expense_category: false,
            rating: false,
            reviews_count: false,
            velocity_group: false,
            domain: false,
            instagram_url: false,
            instagram_followers: false,
            instagram_following: false,
            instagram_posts: false,
            instagram_percentile: false,
            instagram_text_percentile: false,
            ingredients_matrix: false,
            country_summary: false,
            state_summary: false,
            city_summary: false,
            zip_summary: false,
            median_hhi: false,
            pop_density: false,
            hh_gt100k: false,
            reputation_data: false,
            michelin_stars_count: false,
            hq_state2: false,
            hq_city2: false,
          }}
          tableKey={getCampaignTableKey(CHAINS_TABLE_KEY, campaign.id)}
          baseFilters={{
            main_campaign: campaign.id,
            ...(useDenormalized && { view_mode: 'campaign' }),
          }}
        />
      ),
      'education-cu': (
        <UniversitiesTable
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          defaultColumnVisibility={{
            institution_name: true,
            address: false,
            city: false,
            state_abbr: false,
            zipcode: false,
            domain: false,
            dormitory_capacity: false,
            meal_plan_provided: false,
            total_enrollment: false,
            total_men_enrollment: false,
            total_women_enrollment: false,
            food_management_company: false,
            food_management_company_parent: false,
            estimated_meals_per_day: false,
            carnegie_classification_2021_size_setting_size: false,
            carnegie_classification_2021_size_setting_year: false,
            carnegie_classification_2021_size_setting_student_composition: false,
            campaigns: false,
            deal_count: true,
            contact_count: true,
            note_count: true,
            sales_stages: true,
            account_owner: true,
            taglist: true,
            distributors: true,
            google_place_url: false,
          }}
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(
            COLLEGE_AND_UNIVERSITY_TABLE_KEY,
            campaign.id
          )}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'education-k-12': (
        <K12Table
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(K12_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
          defaultColumnVisibility={{
            k12district__lea_name: true,
            k12district__address: false,
            k12district__zipcode: false,
            k12district__lcity: false,
            k12district__lstate: false,
            k12district__county: false,
            k12district__locale: false,
            k12district__operational_schools: false,
            k12district__gslo: false,
            k12district__gshi: false,
            k12district__student_count: false,
            k12district__teachers_count: false,
            k12district__phone: false,
            campaigns: false,
            sales_stages: true,
            account_owners: true,
            taglist: true,
            contact_count: true,
            note_count: true,
            distributors: true,
            deal_count: true,
            k12district__revenue: false,
            k12district__reduced_lunch: false,
            k12district__free_lunch: false,
            k12district__estimated_meals_per_day: false,
            k12district__food_service_expenditures: false,
            k12district__total_supplemented_lunch: false,
            k12district__total_supplemented_lunch_perc: false,
            k12district__domain: false,
            k12district__google_place_url: false,
            k12district__uncategorized_lunch: false,
            k12district__free_lunch_perc: false,
            k12district__reduced_lunch_perc: false,
            k12district__uncategorized_lunch_perc: false,
          }}
        />
      ),
      'healthcare-hospitals': (
        <HospitalsTable
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          defaultColumnVisibility={{
            provider_number: true,
            hospital_name: true,
            street_addr: false,
            po_box: false,
            city: false,
            state: false,
            zip_code: false,
            year: false,
            total_hospital_days: false,
            total_hospital_beds: false,
            year_2: false,
            cafeteria: false,
            dietary: false,
            dietary_under_contract: false,
            campaigns: false,
            sales_stages: true,
            account_owners: true,
            taglist: true,
            contact_count: true,
            note_count: true,
            distributors: true,
            deal_count: true,
          }}
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(HOSPITALS_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'healthcare-nursinghomes': (
        <NursingHomesTable
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(NURSING_HOMES_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'lodging-hospitality': (
        <LodgingTable
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(LODGING_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      convenience: (
        <ConvenienceTable
          setTotalRowsCount={setRowCount}
          hideCreateCompanyButton
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={filterMod}
          tableKey={getCampaignTableKey(CONVENIENCE_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
    }

  const slug = campaign.type?.slug ?? ''
  let table: React.JSX.Element
  if (Object.keys(tableBySlugType).includes(slug)) {
    table = tableBySlugType[slug as CompanyTypeSlugWithFBData]
  } else if (type) {
    table = (
      <CompaniesTable
        setTotalRowsCount={setRowCount}
        subCategory={type.sub_category}
        category={type.category}
        filterIdentifierModifier={filterMod}
        tableKey={getCampaignTableKey(
          getCompanyTableKey(slug as CompanyTypeSlug),
          campaign.id
        )}
        baseFilters={{ campaigns: campaign.id }}
      />
    )
  } else {
    // Multi-type campaign — show generic CompaniesTable without type-specific columns
    table = (
      <CompaniesTable
        setTotalRowsCount={setRowCount}
        filterIdentifierModifier={filterMod}
        tableKey={getCampaignTableKey('multi-type-targets', campaign.id)}
        baseFilters={{ campaigns: campaign.id }}
      />
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <StageColorProvider value="pipeline">
        <div className="flex h-full flex-col gap-3">
          <MissingContactsBanner campaign={campaign} />
          <FBPageTableContainer
            className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
            style={{ flex: 1, minHeight: 0 }}
          >
            {table}
          </FBPageTableContainer>
        </div>
      </StageColorProvider>
    </TableLinkStyleProvider>
  )
}
